// CI/CD pipeline trigger v5 - force both FE and BE build
import { useState, useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useIdentity } from './useIdentity';
import { ReviewPage } from './components/features/review';
import { Toaster } from './components/ui/sonner';
import { useRouteSync } from './hooks';
import { LandingPage, SettingsArea } from './pages';
import { AppHeader } from './components/layout';
import { ErrorBoundary } from './components/common';
import { InitialLoadModal } from './components/features/help';
import { isEvoPlatformAvailable } from './lib/environmentUtils';

function RouteSyncWrapper({ children }: { children: React.ReactNode }) {
  useRouteSync();
  return <>{children}</>;
}

function AuthenticatedApp() {
  const [showLoadNotice, setShowLoadNotice] = useState(
    () => sessionStorage.getItem('loadNoticeDismissed') !== 'true',
  );

  const dismissLoadNotice = (open: boolean) => {
    if (!open) {
      sessionStorage.setItem('loadNoticeDismissed', 'true');
    }
    setShowLoadNotice(open);
  };

  useEffect(() => {
    if (!showLoadNotice) return;
    const timer = setTimeout(() => dismissLoadNotice(false), 60000);
    return () => clearTimeout(timer);
  }, [showLoadNotice]);

  return (
    <ErrorBoundary>
        <RouteSyncWrapper>
          <div className="flex flex-col min-h-screen">
            <AppHeader />
            <main className="flex-1">
              <Routes>
                  <Route path="/" element={<LandingPage />} />
                  <Route path="/review/:projectName/:fiscalYearQuarter" element={<ReviewPage />} />
                  <Route path="/batch-process" element={<Navigate to="/settings" replace />} />
                  <Route path="/settings" element={<SettingsArea />} />
              </Routes>
            </main>
          </div>
          <Toaster />
          <InitialLoadModal open={showLoadNotice} onOpenChange={dismissLoadNotice} />
        </RouteSyncWrapper>
    </ErrorBoundary>
  );
}

function App() {
  const { loading: authLoading, error: authError, isAuthenticated, isRequestingReauth } = useIdentity();

  // When running inside the EVO platform (preprod/production iframe),
  // skip auth UI waiting — EVO handles authentication and provides
  // the access token via getEvoAccessToken() when API calls are made.
  if (!isEvoPlatformAvailable()) {
    if (authLoading) {
      return (
        <div className="flex items-center justify-center h-screen">
          <div className="text-center space-y-4">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto" />
            <p className="text-gray-600">Loading...</p>
          </div>
        </div>
      );
    }

    if (isRequestingReauth || authError || !isAuthenticated) {
      return (
        <div className="flex items-center justify-center h-screen">
          <div className="text-center space-y-4">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto" />
            <p className="text-gray-600">
              {isRequestingReauth ? 'Requesting authentication from platform...' : 'Authenticating...'}
            </p>
          </div>
        </div>
      );
    }
  }

  return <AuthenticatedApp />;
}

export default App
